import EditIcon from '@mui/icons-material/Edit'
import HubIcon from '@mui/icons-material/Hub'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import {
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
} from 'react-admin'
import { Samenwerking } from '../../types/apiRequests'

interface EditSamenwerkingDialogProps {
  record: Samenwerking
  disabled?: boolean
}

const EditSamenwerkingDialog: FC<EditSamenwerkingDialogProps> = ({ record, disabled = false }) => {
  const [open, setOpen] = useState(false)
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [update, { isLoading }] = useUpdate()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const save = (values: Record<string, unknown>) => {
    const data: Record<string, unknown> = {}
    const fields = [
      'titel',
      'beschrijving',
      'samenwerkDoel',
      'globaleLocatie',
      'contactpersoonNaam',
      'contactpersoonEmailadres',
      'contactpersoonTelefoonnummer',
      'nummerBinnenSysteem',
      'kenmerkSysteem',
    ] as const

    for (const field of fields) {
      // Known fields from const array — safe to use bracket notation
      // eslint-disable-next-line security/detect-object-injection
      const newVal = values[field] as string | null | undefined
      // eslint-disable-next-line security/detect-object-injection
      const oldVal = record[field]
      if (newVal !== oldVal) {
        // eslint-disable-next-line security/detect-object-injection
        data[field] = newVal || null
      }
    }

    if (Object.keys(data).length === 0) {
      handleClose()
      return
    }

    void update(
      'samenwerkingen',
      { id: record.id, data, previousData: record },
      {
        onSuccess: () => {
          // eslint-disable-next-line camelcase
          notify('ra.notification.updated', { type: 'info', messageArgs: { smart_count: 1 } })
          handleClose()
          refresh()
        },
        onError: (error: unknown) => {
          const apiError = error as {
            message?: string
            body?: { detail?: string | object[]; message?: string }
          }
          const message =
            apiError?.body?.message ||
            apiError?.message ||
            (typeof apiError?.body?.detail === 'string' ? apiError?.body?.detail : null) ||
            'ra.notification.http_error'
          notify(message, { type: 'warning' })
        },
      }
    )
  }

  return (
    <>
      <EditIcon
        fontSize="small"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          'ml': 'auto',
          'color': disabled ? 'grey.400' : 'primary.main',
          'cursor': disabled ? 'default' : 'pointer',
          ':hover': disabled ? {} : { color: 'primary.dark' },
        }}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HubIcon />
          {translate('data.editSamenwerking')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ ml: 2, mb: 1 }} color="textSecondary">
            {translate('data.editSamenwerkingSubtitle')}
          </Typography>
          <SimpleForm
            onSubmit={save}
            toolbar={
              <Toolbar>
                <SaveButton disabled={isLoading} />
                <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
              </Toolbar>
            }
            defaultValues={{
              titel: record.titel || '',
              beschrijving: record.beschrijving || '',
              samenwerkDoel: record.samenwerkDoel || '',
              globaleLocatie: record.globaleLocatie || '',
              contactpersoonNaam: record.contactpersoonNaam || '',
              contactpersoonEmailadres: record.contactpersoonEmailadres || '',
              contactpersoonTelefoonnummer: record.contactpersoonTelefoonnummer || '',
              nummerBinnenSysteem: record.nummerBinnenSysteem || '',
              kenmerkSysteem: record.kenmerkSysteem || '',
            }}
          >
            <TextInput source="titel" label="data.title" fullWidth />
            <TextInput source="beschrijving" label="data.descriptionCooperation" fullWidth multiline minRows={3} />
            <SelectInput
              source="samenwerkDoel"
              label="data.purpose"
              fullWidth
              choices={[
                { id: 'CONCEPTVERZOEK', name: 'Conceptverzoek' },
                { id: 'BEHANDELEN', name: 'Behandelen' },
                { id: 'TOEZICHT_EN_HANDHAVING', name: 'Toezicht en handhaving' },
              ]}
            />
            <TextInput source="globaleLocatie" label="data.globaleLocatie" fullWidth />
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {translate('resources.collaborations.contactPersonCompetentAuthority')}
            </Typography>
            <TextInput source="contactpersoonNaam" label="data.name" fullWidth />
            <TextInput source="contactpersoonEmailadres" label="data.email" fullWidth />
            <TextInput source="contactpersoonTelefoonnummer" label="data.phone" fullWidth />
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {translate('resources.collaborations.sourceSystem')}
            </Typography>
            <TextInput source="kenmerkSysteem" label="data.name" fullWidth />
            <TextInput source="nummerBinnenSysteem" label="data.caseNumber" fullWidth />
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default EditSamenwerkingDialog
